import * as React from 'react'

import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { VariablesOf } from '~publish/graphql'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'

import { ConversationCard_Conversation } from '../fragments/conversationCard'
import type { ConversationCardData } from '../fragments/conversationCard'

export const CONVERSATIONS_PER_PAGE = 20

export const ConversationsInboxQueryGQL = graphql(
  /* GraphQL */ `
    query ConversationsInbox(
      $input: ConversationsInput!
      $first: Int!
      $after: String
    ) {
      conversations(input: $input, first: $first, after: $after) {
        edges {
          cursor
          node {
            ...ConversationCard_Conversation
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [ConversationCard_Conversation],
)

type ConversationsInboxQueryVariables = VariablesOf<
  typeof ConversationsInboxQueryGQL
>

type UseConversationsQueryProps = {
  /** Only `id` is read, to scope the inbox. Typed structurally on purpose. */
  channels: ReadonlyArray<{ id: string }>
  skip?: boolean
}

export type UseConversationsQueryReturn = {
  conversations: ConversationCardData[]
  loading: boolean
  error: ErrorLike | undefined
  fetchingMore: boolean
  lastElementRef: (node: Element | null) => void
  /**
   * The inbox has answered for these channels and no request is in flight,
   * so `conversations` is what the API returned rather than a cache read a
   * pending request may still change. False while skipped or failed.
   */
  settled: boolean
  /** Reloads the first page, for the error state's retry. */
  refetch: () => Promise<unknown>
}

/**
 * The conversations inbox for the Messages tab, one row per conversation.
 *
 * Sends no filters or sort, so the API's defaults apply: spam hidden,
 * unreplied first, then newest activity. The filter toolbar arrives
 * separately once its design settles, and will add `filters` and `sort` to
 * the variables here.
 *
 * Pages accumulate in the Apollo cache: the client's type policy for
 * `conversations` is `relayStylePagination`, keyed on `input`, so a fetched
 * page is appended to the cached list and this hook reads the whole list back.
 *
 * Access errors are all-or-nothing: a single channel the actor cannot read
 * fails the whole call, so `error` means the list, not one row.
 */
export function useConversationsQuery({
  channels,
  skip = false,
}: UseConversationsQueryProps): UseConversationsQueryReturn {
  const organizationId = useOrganizationId()

  // Sorted and joined so a new array with the same ids does not refetch.
  const channelIdsKey = channels
    .map((channel) => channel.id)
    .toSorted()
    .join('\0')
  const channelIds = React.useMemo(
    () => (channelIdsKey ? channelIdsKey.split('\0') : []),
    [channelIdsKey],
  )

  const variables = React.useMemo<ConversationsInboxQueryVariables>(
    () => ({
      input: {
        organizationId: organizationId ?? '',
        channelIds,
      },
      first: CONVERSATIONS_PER_PAGE,
      after: null,
    }),
    [organizationId, channelIds],
  )

  const { data, loading, error, fetchMore, refetch, networkStatus } = useQuery(
    ConversationsInboxQueryGQL,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: !organizationId || skip || channelIds.length === 0,
    },
  )

  const fetchingMore = networkStatus === NetworkStatus.fetchMore

  const [lastElementRef] = useInfiniteScrollPagination({
    loading: fetchingMore,
    hasNextPage: data?.conversations.pageInfo.hasNextPage,
    fetchMore: async () => {
      try {
        await fetchMore({
          variables: {
            ...variables,
            after: data?.conversations.pageInfo.endCursor ?? null,
          },
        })
      } catch (fetchMoreError) {
        toast.error('Error fetching more conversations')
        throw new Error('Failed to fetch more conversations', {
          cause: fetchMoreError,
        })
      }
    },
  })

  const conversations = React.useMemo<ConversationCardData[]>(
    () => data?.conversations.edges.map((edge) => edge.node) ?? [],
    [data],
  )

  // A failed reload already surfaces through `error`; swallowing the
  // rejection keeps a second failure from also logging as unhandled.
  const refetchFirstPage = React.useCallback(
    (): Promise<unknown> => refetch(variables).catch(() => undefined),
    [refetch, variables],
  )

  return React.useMemo(
    () => ({
      conversations,
      // Apollo reports `loading` during fetchMore too; the list treats paging
      // as its own state so the rows already shown do not flip to a skeleton.
      loading: loading && !fetchingMore,
      error,
      fetchingMore,
      lastElementRef,
      settled: data !== undefined && networkStatus === NetworkStatus.ready,
      refetch: refetchFirstPage,
    }),
    [
      conversations,
      loading,
      error,
      fetchingMore,
      lastElementRef,
      data,
      networkStatus,
      refetchFirstPage,
    ],
  )
}
